import type { ReactNode } from 'react';
/* THE MODULE, NOT THE BARREL (wave 5 round 5, G5). `ui/SettingsGroup` adopts this host so
   that every settings pane gets it once, and `ui/index.ts` re-exports `SettingsGroup`; a
   barrel import here would close that into a cycle - ui/index -> SettingsGroup -> PaneHost
   -> ui/index - whose module order decides whether `cx` exists when this file is evaluated.
   `cx` is a leaf the barrel itself re-exports from this same path. */
import { cx } from '../../ui/cx';

/**
 * THE PANE HOST: THE SCREEN'S VERTICAL SCROLLPORT, AND IT BEGINS AT THE BAR'S TOP EDGE
 * (wave 5 round 0, A0).
 *
 * WHAT IT IS FOR. The window toolbar carries the kit's HARD scroll edge - a 6px background
 * blur under a flat tint with a hairline at its lower edge - and every part of that is a
 * function of what is behind it. A blur of a flat fill is that fill, a displacement map on
 * a constant field returns the constant, and a tint over nothing is a band of paint. So the
 * bar only means anything if content really passes under it, and on both list screens
 * nothing does: the rows live in a clipped column that begins at the bar's LOWER edge and
 * scroll inside it, so every scroller starts at y 52 at 1440 and the bar stands on a flat
 * canvas (review checks (x1) and (x2), red on main for agencies and tasks).
 *
 * APPLE INSTANCES THE OTHER CONSTRUCTION RATHER THAN DESCRIBING IT. `kit:` Apple macOS 27
 * UI Kit.sketch / `Windows/Dark/Unified Toolbar + Title + Sidebar/Active/Right Pane`: the `Scroll Edge Effect` instance sits at (0,0,584,52) OVER the `Scroll View`, so
 * the scroll view is the WHOLE pane and the bar's band is the top 52 of it. `wwdc:`
 * https://developer.apple.com/videos/play/wwdc2026/102/ at 24:00 - "When content scrolls
 * under floating bars, a uniform toolbar appears across the top". `hig:`
 * https://developer.apple.com/design/human-interface-guidelines/scroll-views - one effect
 * per view.
 *
 * THE THREE RULES FOR A SCREEN THAT ADOPTS IT (`docs/decisions/ui-foundation-w5.md`, A0):
 * the host is the screen's ONE vertical scrollport; a horizontal pan, such as the record
 * table's column region, lives inside it as `overflow-x` and never as a second vertical
 * scroller; and a first line that has to stay put uses `PanePinned` and mounts its own
 * `ScrollEdge`, because the pinned row paints no ground of its own.
 *
 * WHO MOUNTS IT. The object list and board, tasks, the record, reports and a report's
 * detail mount it directly, every settings pane gets it through `ui/SettingsGroup`, and
 * `/dev/shell-states` mounts it as the specimen check (x3) reads.
 */
export interface PaneHostProps {
  /**
   * THE NAME THE REVIEW ENUMERATES THIS PANE BY, and the reason the prop is required - the
   * same argument `ScrollEdge` makes for its own `host`: a pane with no name can only be
   * reported as a selector nobody can look up. It lands on `data-pane-host`, and a screen
   * that mounts two panes (a split view) gives each its own name.
   */
  readonly host: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function PaneHost({ host, children, className }: PaneHostProps) {
  return (
    <div className={cx('shell-pane-host', className)} data-pane-host={host}>
      {children}
    </div>
  );
}

/**
 * THE LINE THAT STAYS AT THE BAR'S LOWER EDGE while the rows pass under it: a column head,
 * a list-controls row, a count. It is sticky INSIDE the host rather than a sibling above
 * it, because a sibling above the host would take the host out of the bar's way and put the
 * flat strip straight back.
 */
export function PanePinned({ children }: { readonly children: ReactNode }) {
  return <div className="shell-pane-pinned">{children}</div>;
}
